/**
 * Technician Assignment Section Component
 * SRV-FG-5-9: Technikus hozzarendeles vedelem (max 2 co-tech)
 *
 * Displays assigned technicians with primary/co-tech badges.
 * Enforces max 2 co-technician limit.
 */

import {
  assignWorksheetTechnician,
  getWorksheetTechnicians,
  removeWorksheetTechnician,
  type TechnicianAssignment,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

interface TechnicianAssignmentSectionProps {
  worksheetId: string;
  isEditable: boolean;
}

export function TechnicianAssignmentSection({
  worksheetId,
  isEditable,
}: TechnicianAssignmentSectionProps) {
  const { t } = useTranslation();
  const [technicians, setTechnicians] = useState<TechnicianAssignment[]>([]);
  const [canAddMore, setCanAddMore] = useState(true);
  const [maxCoTechnicians, setMaxCoTechnicians] = useState(2);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTechName, setNewTechName] = useState('');
  const [newTechRole, setNewTechRole] = useState<'PRIMARY' | 'CO_TECH'>('CO_TECH');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTechnicians = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorksheetTechnicians(worksheetId);
      setTechnicians(response.data.technicians);
      setCanAddMore(response.data.canAddMore);
      setMaxCoTechnicians(response.data.maxCoTechnicians);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    void fetchTechnicians();
  }, [fetchTechnicians]);

  const handleAssign = async () => {
    setError(null);

    if (!newTechName.trim() || newTechName.trim().length < 2) {
      setError(t('worksheet.technician.nameRequired'));
      return;
    }

    setSaving(true);
    try {
      // Generate a pseudo-ID from the name for MVP
      const userId = `tech-${newTechName.trim().toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
      const response = await assignWorksheetTechnician(worksheetId, {
        userId,
        name: newTechName.trim(),
        role: newTechRole,
      });
      setTechnicians(response.data.technicians);
      setCanAddMore(response.data.canAddMore);
      setNewTechName('');
      setNewTechRole('CO_TECH');
      setShowAddForm(false);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.unknownError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      const response = await removeWorksheetTechnician(worksheetId, userId);
      setTechnicians(response.data.technicians);
      setCanAddMore(response.data.canAddMore);
    } catch {
      // Non-critical
    }
  };

  const getRoleBadgeColor = (role: 'PRIMARY' | 'CO_TECH'): string => {
    if (role === 'PRIMARY') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
  };

  const getRoleLabel = (role: 'PRIMARY' | 'CO_TECH'): string => {
    return role === 'PRIMARY'
      ? t('worksheet.technician.primary')
      : t('worksheet.technician.coTech');
  };

  const hasPrimary = technicians.some(tech => tech.role === 'PRIMARY');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {t('worksheet.technician.title')}
          </CardTitle>
          {isEditable && (
            <span className="text-xs text-muted-foreground">
              {t('worksheet.technician.maxCoTechInfo', { max: maxCoTechnicians })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-full" />
            <div className="h-8 bg-muted rounded w-3/4" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Technician list */}
            {technicians.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('worksheet.technician.noAssigned')}
              </p>
            ) : (
              <div className="space-y-2">
                {technicians.map(tech => (
                  <div
                    key={tech.userId}
                    className="flex items-center justify-between rounded-lg border border-border p-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                        {tech.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{tech.name}</p>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(tech.role)}`}
                        >
                          {getRoleLabel(tech.role)}
                        </span>
                      </div>
                    </div>
                    {isEditable && (
                      <button
                        onClick={() => handleRemove(tech.userId)}
                        className="text-muted-foreground hover:text-red-500 transition-colors"
                        title={t('common.remove')}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Max reached warning */}
            {!canAddMore && isEditable && (
              <div className="rounded-lg border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 p-2.5">
                <p className="text-xs text-yellow-700 dark:text-yellow-400 flex items-center gap-1.5">
                  <svg
                    className="h-4 w-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  {t('worksheet.technician.maxReached', { max: maxCoTechnicians })}
                </p>
              </div>
            )}

            {/* Add technician form */}
            {isEditable && !showAddForm && (canAddMore || !hasPrimary) && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowAddForm(true)}
              >
                {t('worksheet.technician.addTechnician')}
              </Button>
            )}

            {showAddForm && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('worksheet.technician.nameLabel')}
                  </label>
                  <Input
                    value={newTechName}
                    onChange={e => setNewTechName(e.target.value)}
                    placeholder={t('worksheet.technician.namePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('worksheet.technician.roleLabel')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewTechRole('PRIMARY')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        newTechRole === 'PRIMARY'
                          ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-600'
                          : 'bg-background text-muted-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {t('worksheet.technician.primary')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewTechRole('CO_TECH')}
                      disabled={!canAddMore}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        newTechRole === 'CO_TECH'
                          ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-300 dark:border-green-600'
                          : 'bg-background text-muted-foreground border-border hover:bg-muted'
                      } ${!canAddMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {t('worksheet.technician.coTech')}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAssign} disabled={saving}>
                    {saving ? t('common.saving') : t('common.add')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setError(null);
                      setNewTechName('');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
